/**
 * screenshotLibrary — JS bridge for Auto Scan's Screenshots-album sweep.
 *
 * Wraps the native ScreenshotLibrary plugin (iOS: Photos framework reading
 * the Screenshots smart album; see ios/App/App/Plugins/ScreenshotLibrary/).
 * Unlike nativePicker.pickImages (PHPicker, manual selection), this lets
 * Auto Scan process every NEW screenshot without the trainer picking them —
 * pressing Auto Scan is the act of choosing to share.
 *
 * The sweep is incremental: a device-local cursor remembers the newest
 * screenshot creation time already processed, so each run only reads what's
 * new. The first run starts from the most recent screenshots (newest
 * SWEEP_LIMIT of them), not the whole multi-year album.
 *
 * Web / Android / plugin-missing builds: isScreenshotSweepAvailable() is
 * false and Auto Scan keeps its picker flow.
 */
import { Capacitor, registerPlugin } from "@capacitor/core";
import { getPlatform } from "./platform";

/**
 * "restricted" = Photos is blocked device-wide (Screen Time / parental
 * controls / MDM): the system prompt never fires and Settings shows NO
 * Photos row for the app, so it must never be messaged as "allow it in
 * Settings". Old binaries report it as "denied".
 */
export type ScreenshotAccessStatus =
  | "granted"
  | "limited"
  | "denied"
  | "restricted"
  | "notDetermined";

interface ScreenshotItem {
  id: string;
  createdAt: number;
}

interface ScreenshotLibraryPlugin {
  getStatus(): Promise<{ status: ScreenshotAccessStatus }>;
  requestAccess(): Promise<{ status: ScreenshotAccessStatus }>;
  listScreenshots(opts: {
    afterMs?: number;
    limit?: number;
    /** Take the newest `limit` (still returned ascending). First-run path. */
    newestFirst?: boolean;
  }): Promise<{ items: ScreenshotItem[]; total: number }>;
  readScreenshot(opts: { id: string }): Promise<{ base64: string; mime: string }>;
  openSettings(): Promise<void>;
}

const plugin = registerPlugin<ScreenshotLibraryPlugin>("ScreenshotLibrary");

/** Max screenshots one sweep run processes (first run: the newest this-many). */
export const SWEEP_LIMIT = 300;

const CURSOR_KEY = "nexus.autoScanCursor.v1";

export function isScreenshotSweepAvailable(): boolean {
  return getPlatform() === "ios" && Capacitor.isPluginAvailable("ScreenshotLibrary");
}

export function getSweepCursor(): number {
  try {
    const v = Number(localStorage.getItem(CURSOR_KEY));
    return Number.isFinite(v) && v > 0 ? v : 0;
  } catch {
    return 0;
  }
}

export function setSweepCursor(ms: number): void {
  try {
    const cur = getSweepCursor();
    if (ms > cur) localStorage.setItem(CURSOR_KEY, String(ms));
  } catch {
    // Storage blocked: the sweep just re-reads next time; processing is
    // idempotent (content-hash dedupe + additive writes downstream).
  }
}

/**
 * Read the current photo-library grant WITHOUT ever prompting. The on-open
 * sweep (autoScanOnOpen.ts) uses this: a permission dialog on launch, with
 * nothing the trainer just tapped to explain it, is exactly what Apple's
 * guidelines and common sense both forbid. "notDetermined" comes back as-is
 * and the caller treats it as "not today".
 */
export async function peekScreenshotAccess(): Promise<ScreenshotAccessStatus> {
  try {
    return (await plugin.getStatus()).status;
  } catch {
    return "notDetermined";
  }
}

/** Ask for (or confirm) photo-library access. */
export async function ensureScreenshotAccess(): Promise<ScreenshotAccessStatus> {
  const { status } = await plugin.getStatus();
  if (status !== "notDetermined") return status;
  return (await plugin.requestAccess()).status;
}

/**
 * Open this app's page in the Settings app (where the Photos toggle lives).
 * False when the binary predates the openSettings method.
 */
export async function openAppSettings(): Promise<boolean> {
  try {
    await plugin.openSettings();
    return true;
  } catch {
    return false;
  }
}

export interface SweepPlan {
  items: ScreenshotItem[];
  /** Album total matching the query, before the SWEEP_LIMIT cap. */
  total: number;
}

/**
 * The screenshots this run should process, oldest first so the cursor can
 * advance monotonically. First run (no cursor): the newest SWEEP_LIMIT of
 * the whole album. Later runs: everything newer than the cursor (capped).
 */
export async function planSweep(): Promise<SweepPlan> {
  const afterMs = getSweepCursor();
  if (afterMs > 0) {
    const { items, total } = await plugin.listScreenshots({ afterMs, limit: SWEEP_LIMIT });
    return { items, total };
  }
  // First run: ask the native side for the newest window directly. The old
  // shape here (fetch "everything" and slice) never worked: the native layer
  // caps a fetch at 1000 and enumerates OLDEST-first, so the slice kept the
  // old end of a big album and every sweep re-crawled ancient screenshots.
  const { items, total } = await plugin.listScreenshots({
    limit: SWEEP_LIMIT,
    newestFirst: true,
  });
  return { items, total };
}

/** Read one screenshot as a File ready for the scan pipeline. */
export async function readScreenshotFile(item: ScreenshotItem, index: number): Promise<File> {
  const { base64, mime } = await plugin.readScreenshot({ id: item.id });
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  return new File([bytes], `screenshot-${index + 1}.jpg`, {
    type: mime || "image/jpeg",
    // The photo's own time, so a reading off it (the stardust balance) is
    // dated by when the game showed it, not by when it was scanned.
    lastModified: item.createdAt > 0 ? item.createdAt : Date.now(),
  });
}

/* ── Held-for-review queue ───────────────────────────────────────────────── */

/**
 * Screenshots a BACKGROUND Auto Scan could not save on its own (uncertain
 * variant, nickname, Pro lock). Only asset IDS are stored — the images stay
 * in Photos and are re-read on demand when the trainer opens the review.
 * The sweep cursor advances past held items, so this queue is the only
 * pointer back to them.
 */
const HELD_KEY = "nexus.autoScanHeld.v1";

export function listHeldScreenshots(): ScreenshotItem[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(HELD_KEY) ?? "[]") as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (x): x is ScreenshotItem =>
        !!x && typeof (x as ScreenshotItem).id === "string" &&
        typeof (x as ScreenshotItem).createdAt === "number",
    );
  } catch {
    return [];
  }
}

export function addHeldScreenshot(item: ScreenshotItem): void {
  try {
    const cur = listHeldScreenshots();
    if (cur.some((h) => h.id === item.id)) return;
    localStorage.setItem(HELD_KEY, JSON.stringify([...cur, item]));
  } catch {
    // Storage blocked: the item is simply not held; nothing was saved wrongly.
  }
}

export function clearHeldScreenshots(): void {
  try {
    localStorage.removeItem(HELD_KEY);
  } catch {
    // ignore
  }
}
